<template>
  <div ref="observerElement">
    <video
      ref="videoElement"
      class="video-js"
      preload="metadata"
      width="640"
      height="264"
      playsinline
      muted
    >
      <source
        :src="source"
        type="application/x-mpegURL"
      >
    </video>
    <div
      v-if="showError"
      class="video-error"
    >
      <div class="camera-offline-icon">
        <feather-icon
          icon="VideoOffIcon"
          size="48"
          class="error-icon"
        />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import videojs from 'video.js'
// eslint-disable-next-line import/no-extraneous-dependencies
require('video.js/dist/video-js.css')

export default {
  components: {
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['source', 'index'],
  data() {
    return {
      observer: null,
      showError: false,
      retryTimeout: null,
      retryAttempted: 0,
      maxRetries: 3,
      isDisposed: false,
    }
  },
  mounted() {
    this.initializePlayer()
    this.setupIntersectionObserver()
    // Play the first four videos immediately
    if (this.index < 4) {
      this.player.play().catch(error => console.log(error))
    }
  },
  beforeDestroy() {
    this.disposePlayer()
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }
  },
  methods: {
    initializePlayer() {
      this.player = videojs(this.$refs.videoElement, {
        html5: {
          hls: {
            smoothQualityChange: true,
            maxBufferLength: 30,
            maxBufferSize: 60 * 1000 * 1000, // 60MB
            overrideNative: true,
            enableLowInitialPlaylist: true,
          },
        },
        liveui: true,
        errorDisplay: false,
      }, () => {
        this.player.on('error', this.handleError)
        this.player.on('loadedmetadata', () => {
          this.retryAttempted = 0
          this.showError = false
        })
      })
    },
    disposePlayer() {
      if (this.player && !this.isDisposed) {
        this.player.off('error', this.handleError)
        this.player.off('loadedmetadata')
        this.player.dispose()
        this.isDisposed = true
      }
    },
    handleError() {
      const error = this.player ? this.player.error() : null
      const errorCode = error ? error.code : 0

      console.log(`Video error: ${errorCode}`, this.source)

      // Only retry if we haven't exceeded max retries
      if (this.retryAttempted < this.maxRetries) {
        this.retryAttempted += 1
        this.showError = true

        console.log(`Retry attempt ${this.retryAttempted} of ${this.maxRetries} for ${this.source}`)

        // Exponential backoff for retries: 2s, 4s, 8s
        const retryDelay = 2 ** this.retryAttempted * 1000

        // Clear any existing timeout
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout)
        }

        this.retryTimeout = setTimeout(() => {
          if (this.isDisposed) return // Don't retry if already disposed

          // Recreate player if it was disposed
          if (!this.player || this.player.isDisposed()) {
            this.isDisposed = false
            this.initializePlayer()
          } else {
            // Reset the player and try again
            this.showError = false
            this.player.reset()
            this.player.src({ type: 'application/x-mpegURL', src: this.source })
            this.player.load()

            // Try to play but handle rejection
            try {
              const playPromise = this.player.play()
              if (playPromise !== undefined) {
                playPromise.catch(playError => console.log('Play error during retry:', playError))
              }
            } catch (e) {
              console.log('Exception during retry play:', e)
            }
          }
        }, retryDelay)
      } else {
        console.log('Max retries exceeded for', this.source)
        this.showError = true
        this.$emit('video-error', this.index)
      }
    },
    setupIntersectionObserver() {
      const options = {
        root: null,
        threshold: 0.5,
      }
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.player && !this.isDisposed && !this.showError) {
            try {
              if (this.player.paused()) {
                const playPromise = this.player.play()
                if (playPromise !== undefined) {
                  playPromise.catch(err => {
                    console.warn('Intersection observer playback was prevented:', err)
                  })
                }
              }
            } catch (error) {
              console.warn('Error during play:', error)
            }
          } else if (this.player && !this.isDisposed) {
            try {
              this.player.pause()
            } catch (error) {
              console.warn('Error during pause:', error)
            }
          }
        })
      }, options)
      this.observer.observe(this.$refs.observerElement)
    },
  },
}
</script>

<style scoped>
.video-js {
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
}

.video-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
}

.camera-offline-icon {
  width: 80px;
  height: 80px;
}

.error-icon {
  width: 48px;
  height: 48px;
  color: #ffa600;
  stroke-width: 1.5;
}
</style>
